import { useFormatter, useLocale } from 'next-intl';

export const convertDayToDate = (dayIndex: number) => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + dayIndex);
  return currentDate.toISOString().split('T')[0];
};

export const formatRelativeDate = (dateStr: string, lang: string): string => {
  // Parse the input date string in "YYYY-MM-DD" format
  const [year, month, day] = dateStr.split('-').map(Number);
  const targetDate = new Date(year, month - 1, day); // JS Date months are 0-based

  const today = new Date();
  // Reset the time part of today's date to 00:00:00 for accurate day comparison
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Compare the target date with today and tomorrow
  if (targetDate.getTime() === today.getTime()) {
    return lang === 'ar' ? 'اليوم' : 'Today'; // Return "Today" in the appropriate language
  } else if (targetDate.getTime() === tomorrow.getTime()) {
    return lang === 'ar' ? 'غدًا' : 'Tomorrow'; // Return "Tomorrow" in the appropriate language
  } else {
    // Return the day of the week in the appropriate language
    return new Intl.DateTimeFormat(lang, { weekday: 'long' }).format(targetDate);
  }
};

export const convertUTCToLocalTime = (utcDateStr: string, lang: string): string => {
  const date = new Date(utcDateStr); // Parse the UTC date string
  return new Intl.DateTimeFormat(lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // Use 12-hour format to show AM/PM
  }).format(date); // Convert to local time string with specific language
};

export const formatDateTime = (inputDateTime: string, lang: 'ar' | 'en') => {
  // Create a new Date object from the input, which is in UTC (because of the 'Z')
  const date = new Date(inputDateTime);

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: 'Asia/Riyadh', // Convert to Saudi Arabia's time zone (UTC+3)
  };

  // Convert simplified language code to appropriate locale for Saudi Arabia
  const locale = lang === 'ar' ? 'ar-SA' : 'en-US';

  const formattedDate = date.toLocaleString(locale, options);

  return formattedDate;
};

export const generateTimeIntervals = (currentDay: string, startHour: string, endHour: string) => {
  const intervals = [];
  // const startTimefrom = startHour;
  const currentDateTime = new Date();
  const nextHour = (currentDateTime.getHours() + 1) % 24;
  currentDateTime.setHours(nextHour, 0, 0, 0);

  const startTime = new Date(
    `${currentDay} ${currentDateTime.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`,
  );

  const endTime = new Date(`${currentDay} ${endHour}`);

  for (
    let currentTime = new Date(startTime);
    currentTime <= endTime;
    currentTime.setMinutes(currentTime.getMinutes() + 30)
  ) {
    const endIntervalTime = new Date(currentTime);
    endIntervalTime.setMinutes(endIntervalTime.getMinutes() + 30);

    intervals.push({
      startHour: currentTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      endHour: endIntervalTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    });
  }

  return intervals;
};

export const ConvertTo12 = (time: string) => {
  const format = useFormatter();

  const [hours, minutes] = time.split(':');
  const dateTime = new Date();
  dateTime.setHours(parseInt(hours));
  dateTime.setMinutes(parseInt(minutes));

  format.dateTime(dateTime, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  return format.dateTime(dateTime, {
    hour: 'numeric',
    minute: 'numeric',
    formatMatcher: 'basic',
  });
};

export const ConvertTimeTo12HourFormat = (time24: string): string => {
  const lang = useLocale();
  let [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
  const englishTime = `${hours}:${formattedMinutes} ${period}`;
  const arabicTime = `${hours}:${formattedMinutes} ${period === 'AM' ? 'ص' : 'م'}`;

  return lang === 'ar' ? arabicTime : englishTime;
};
